import { isAnyOf, UnknownAction } from '@reduxjs/toolkit'
import { createReducer } from '@reduxjs/toolkit/react'
import { TimeType } from '@repo/shared/dtos/generic'
import { PromptTemplateType } from '@repo/shared/dtos/organization'
import { AsyncData, DataStatus } from '@repo/ui/utils'

import { adminStoreActions, storeActions } from '@/store/actions.ts'
import { Beneficiary } from '@/store/beneficiary'
import { ChatMessage } from '@/store/chat'
import { CaseWorker } from '@/store/user'

export type Feedback = {
  beneficiaryId: string
  chatSessionId: string
  content: string
  createdAt: TimeType
  id: string
  message: ChatMessage
}

export type Resource = {
  content: string
  createdAt: TimeType
  id: string
  title: string
  updatedAt: TimeType
}

export type PromptTemplate = {
  id: string
  promptTemplateType: PromptTemplateType
  revision: number
  revisionDate: TimeType
  promptText: string
  keywords: PromptKeyword[]
}

export type PromptKeyword = {
  key: string
  section: string
  apply: string
  isIterable: boolean
}

export type PromptError = {
  name: string
  message: string
  text: string
  line: number
  col: number
}

export type AdminState = {
  beneficiaries: AsyncData<Beneficiary[]>
  caseWorkers: AsyncData<CaseWorker[]>
  promptTemplates: AsyncData<{ [key in PromptTemplateType]?: PromptTemplate }>
  resources: AsyncData<Resource[]>
  feedbacks: AsyncData<Feedback[]>
}

export const adminInitialState: AdminState = {
  beneficiaries: DataStatus.Uninitialized,
  caseWorkers: DataStatus.Uninitialized,
  promptTemplates: DataStatus.Uninitialized,
  resources: DataStatus.Uninitialized,
  feedbacks: DataStatus.Uninitialized,
}

const updateResources = (state: AdminState, resources: Resource[]) => {
  if (!DataStatus.isFulfilled(state.resources)) return (state.resources = resources)
  state.resources = state.resources.map((resource) => {
    const updatedResource = resources.find(({ id }) => id === resource.id)
    return updatedResource ? updatedResource : resource
  })
}

export const adminReducer: ((
  state: AdminState | undefined,
  action: UnknownAction,
) => AdminState) & {
  getInitialState: () => AdminState
} = createReducer(adminInitialState, (builder) => {
  builder.addCase(storeActions.cleanUpOrganization, () => {
    return adminInitialState
  })
  builder.addCase(storeActions.initOrganization.fulfilled, () => {
    return adminInitialState
  })

  builder
    .addCase(adminStoreActions.initAdmin.pending, (state) => {
      Object.keys(state).forEach((key) => {
        if (key === 'promptTemplates') return
        state[key as keyof AdminState] = DataStatus.Loading
      })
    })
    .addCase(adminStoreActions.initAdmin.rejected, (state) => {
      Object.keys(state).forEach((key) => (state[key as keyof AdminState] = DataStatus.Error))
    })
    .addCase(
      adminStoreActions.initAdmin.fulfilled,
      (state, { payload: { beneficiaries, caseWorkers, resources } }) => {
        state.beneficiaries = beneficiaries
        state.caseWorkers = caseWorkers
        state.resources = resources
      },
    )

  builder
    .addCase(adminStoreActions.promptTemplate.init.pending, (state) => {
      state.promptTemplates = DataStatus.Loading
    })
    .addCase(adminStoreActions.promptTemplate.init.rejected, (state) => {
      state.promptTemplates = DataStatus.Error
    })
    .addCase(
      adminStoreActions.promptTemplate.init.fulfilled,
      (state, { payload: { promptTemplates } }) => {
        const indexedPromptTemplates: {
          [key in PromptTemplateType]?: PromptTemplate
        } = {}
        for (const promptTemplate of promptTemplates) {
          indexedPromptTemplates[promptTemplate.promptTemplateType] = promptTemplate
        }
        state.promptTemplates = indexedPromptTemplates
      },
    )

  builder.addCase(
    adminStoreActions.promptTemplate.updateTemplate.fulfilled,
    (state, { payload }) => {
      if ('errors' in payload) return
      const { promptTemplate } = payload
      const indexedPromptTemplates: {
        [key in PromptTemplateType]?: PromptTemplate
      } = state.promptTemplates as {
        [key in PromptTemplateType]?: PromptTemplate
      }
      indexedPromptTemplates[promptTemplate.promptTemplateType] = promptTemplate
      state.promptTemplates = indexedPromptTemplates
    },
  )

  builder
    .addCase(adminStoreActions.resource.update.fulfilled, (state, { payload: { resource } }) => {
      updateResources(state, [resource])
    })
    .addCase(adminStoreActions.resource.create.fulfilled, (state, { payload: { resource } }) => {
      if (!DataStatus.isFulfilled(state.resources)) state.resources = []
      state.resources.push(resource)
    })
    .addCase(
      adminStoreActions.resource.delete.fulfilled,
      (
        state,
        {
          meta: {
            arg: { resourceToDelete },
          },
        },
      ) => {
        if (DataStatus.isFulfilled(state.resources)) {
          state.resources = state.resources.filter((t) => resourceToDelete.id !== t.id)
        }
      },
    )
    .addCase(adminStoreActions.resource.restore.fulfilled, (state, { payload: { resource } }) => {
      if (!DataStatus.isFulfilled(state.resources)) state.resources = []
      state.resources.push(resource)
    })

  builder.addCase(
    adminStoreActions.caseWorker.invite.fulfilled,
    (state, { payload: { caseWorker } }) => {
      if (DataStatus.isFulfilled(state.caseWorkers)) {
        state.caseWorkers.push(caseWorker)
      }
    },
  )

  builder
    .addCase(adminStoreActions.feedback.initFeedbacks.pending, (state) => {
      state.feedbacks = DataStatus.Loading
    })
    .addCase(adminStoreActions.feedback.initFeedbacks.rejected, (state) => {
      state.feedbacks = DataStatus.Error
    })
    .addCase(
      adminStoreActions.feedback.initFeedbacks.fulfilled,
      (state, { payload: { feedbacks } }) => {
        state.feedbacks = feedbacks
      },
    )

  builder.addMatcher(
    isAnyOf(
      adminStoreActions.caseWorker.disableOrEnable.fulfilled,
      adminStoreActions.caseWorker.update.fulfilled,
    ),
    (state, { payload: { caseWorker } }) => {
      if (DataStatus.isFulfilled(state.caseWorkers)) {
        state.caseWorkers = state.caseWorkers.map((cw) =>
          cw.id === caseWorker.id ? caseWorker : cw,
        )
      }
    },
  )
})
